import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { PublicLayout } from "@/layouts/PublicLayout";
// Eager-loaded pages (fast first paint)
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { LandingPage } from "@/pages/LandingPage";
import { SeoPage } from "@/pages/seo/SeoPage";
import { SEO_PAGES } from "@/pages/seo/data";
import "@/pages/seo/seo.css";
// Auth pages (small, keep eager for auth flow)
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage";
import { UnsubscribePage } from "@/pages/UnsubscribePage";
import { AcceptArtistInvitePage } from "@/pages/AcceptArtistInvitePage";
import { AcceptCrewInvitePage } from "@/pages/AcceptCrewInvitePage";
import { AcceptInvitePage } from "@/pages/AcceptInvitePage";
// Eagerly loaded list pages (common navigation)
import { FestivalListPage } from "@/pages/FestivalListPage";
import { RiderUploadPage } from "@/pages/RiderUploadPage";
import { RiderListPage } from "@/pages/RiderListPage";
import { OrganizationPage } from "@/pages/OrganizationPage";
import { PublicDiscoveryPage } from "@/pages/PublicDiscoveryPage";
import { PublicTimetablePage } from "@/pages/PublicTimetablePage";
// Artist portal tab stubs (small, eager-loaded)
import { OverviewTab } from "@/pages/artist-portal/tabs/OverviewTab";
import { LineupTab } from "@/pages/artist-portal/tabs/LineupTab";
import { ItineraryTab } from "@/pages/artist-portal/tabs/ItineraryTab";
import { DocumentsTab } from "@/pages/artist-portal/tabs/DocumentsTab";
import { EmergencyContactsTab } from "@/pages/artist-portal/tabs/EmergencyContactsTab";

// Lazy-loaded pages
const FestivalDetailPage = lazy(() => import("./pages/FestivalDetailPage").then(m => ({ default: m.FestivalDetailPage })));
const ArtistDashboardPage = lazy(() => import("./pages/ArtistDashboardPage").then(m => ({ default: m.ArtistDashboardPage })));
const ArtistPortalDetailPage = lazy(() => import("./pages/artist-portal/ArtistPortalDetailPage").then(m => ({ default: m.ArtistPortalDetailPage })));
const MyShiftsPage = lazy(() => import("./pages/MyShiftsPage").then(m => ({ default: m.MyShiftsPage })));
const CrewManagementPage = lazy(() => import("./pages/CrewManagementPage").then(m => ({ default: m.CrewManagementPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const ProductTourPage = lazy(() => import("./pages/ProductTourPage").then(m => ({ default: m.ProductTourPage })));
const ReviewPage = lazy(() => import("./pages/ReviewPage").then(m => ({ default: m.ReviewPage })));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage").then(m => ({ default: m.AdminUsersPage })));
const AdminUserDetailPage = lazy(() => import("./pages/admin/AdminUserDetailPage").then(m => ({ default: m.AdminUserDetailPage })));
const AdminFestivalsPage = lazy(() => import("./pages/admin/AdminFestivalsPage").then(m => ({ default: m.AdminFestivalsPage })));
const AdminOrgsPage = lazy(() => import("./pages/admin/AdminOrgsPage").then(m => ({ default: m.AdminOrgsPage })));
const AdminUsagePage = lazy(() => import("./pages/admin/AdminUsagePage").then(m => ({ default: m.AdminUsagePage })));
const AdminFunnelPage = lazy(() => import("./pages/admin/AdminFunnelPage").then(m => ({ default: m.AdminFunnelPage })));
const AdminCampaignsPage = lazy(() => import("./pages/admin/AdminCampaignsPage").then(m => ({ default: m.AdminCampaignsPage })));
const AdminCampaignEditorPage = lazy(() => import("./pages/admin/AdminCampaignEditorPage").then(m => ({ default: m.AdminCampaignEditorPage })));
const AdminSupportPage = lazy(() => import("./pages/admin/AdminSupportPage").then(m => ({ default: m.AdminSupportPage })));
const DesignSystemPage = lazy(() => import("./pages/DesignSystemPage").then(m => ({ default: m.DesignSystemPage })));
const EquipmentImageSandboxPage = lazy(() => import("./pages/EquipmentImageSandboxPage").then(m => ({ default: m.EquipmentImageSandboxPage })));

const LazyFallback = (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin h-8 w-8 border-2 border-cyan-400 border-t-transparent rounded-full" />
  </div>
);

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={LazyFallback}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Public landing page
  { path: "/", element: <LandingPage /> },
  // Public SEO content pages (prerendered + hydrated; see scripts/prerender.mjs)
  ...SEO_PAGES.map((p) => ({ path: p.path, element: <SeoPage page={p} /> })),
  // Public timetable (no auth)
  {
    element: <ErrorBoundary><PublicLayout /></ErrorBoundary>,
    children: [
      { path: "/live", element: <PublicDiscoveryPage /> },
      { path: "/live/:slug", element: <PublicTimetablePage /> },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/verify-email", element: <VerifyEmailPage /> },
      { path: "/unsubscribe", element: <UnsubscribePage /> },
      { path: "/accept-invite", element: <AcceptInvitePage /> },
      { path: "/accept-artist-invite", element: <AcceptArtistInvitePage /> },
      { path: "/accept-crew-invite", element: <AcceptCrewInvitePage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <ErrorBoundary><AppLayout /></ErrorBoundary>,
        children: [
          {
            element: <ProtectedRoute superadminOnly />,
            children: [
              { path: "/design-system", element: <SuspenseWrapper><DesignSystemPage /></SuspenseWrapper> },
            ],
          },
          { path: "/sandbox/equipment-images", element: <SuspenseWrapper><EquipmentImageSandboxPage /></SuspenseWrapper> },
          { path: "/dashboard", element: <DashboardPage /> },
          { path: "/learn", element: <SuspenseWrapper><ProductTourPage /></SuspenseWrapper> },
          { path: "/learn/:chapterId", element: <SuspenseWrapper><ProductTourPage /></SuspenseWrapper> },
          { path: "/festivals", element: <FestivalListPage /> },
          { path: "/festivals/:id", element: <SuspenseWrapper><FestivalDetailPage /></SuspenseWrapper> },
          { path: "/riders", element: <RiderListPage /> },
          { path: "/riders/upload", element: <RiderUploadPage /> },
          { path: "/riders/:id/review", element: <SuspenseWrapper><ReviewPage /></SuspenseWrapper> },
          { path: "/settings", element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper> },
          { path: "/organizations/:id", element: <OrganizationPage /> },
          { path: "/my-schedule", element: <SuspenseWrapper><ArtistDashboardPage /></SuspenseWrapper> },
          {
            path: "/my-schedule/:festivalId",
            element: <SuspenseWrapper><ArtistPortalDetailPage /></SuspenseWrapper>,
            children: [
              { index: true, element: <OverviewTab /> },
              { path: "lineup", element: <LineupTab /> },
              { path: "itinerary", element: <ItineraryTab /> },
              { path: "documents", element: <DocumentsTab /> },
              { path: "emergency", element: <EmergencyContactsTab /> },
            ],
          },
          { path: "/my-shifts", element: <SuspenseWrapper><MyShiftsPage /></SuspenseWrapper> },
          { path: "/crew", element: <SuspenseWrapper><CrewManagementPage /></SuspenseWrapper> },
          // Admin routes
          { path: "/admin", element: <SuspenseWrapper><AdminDashboardPage /></SuspenseWrapper> },
          { path: "/admin/users", element: <SuspenseWrapper><AdminUsersPage /></SuspenseWrapper> },
          { path: "/admin/users/:userId", element: <SuspenseWrapper><AdminUserDetailPage /></SuspenseWrapper> },
          { path: "/admin/festivals", element: <SuspenseWrapper><AdminFestivalsPage /></SuspenseWrapper> },
          { path: "/admin/organizations", element: <SuspenseWrapper><AdminOrgsPage /></SuspenseWrapper> },
          { path: "/admin/usage", element: <SuspenseWrapper><AdminUsagePage /></SuspenseWrapper> },
          { path: "/admin/funnel", element: <SuspenseWrapper><AdminFunnelPage /></SuspenseWrapper> },
          { path: "/admin/campaigns", element: <SuspenseWrapper><AdminCampaignsPage /></SuspenseWrapper> },
          { path: "/admin/campaigns/:campaignId", element: <SuspenseWrapper><AdminCampaignEditorPage /></SuspenseWrapper> },
          { path: "/admin/support", element: <SuspenseWrapper><AdminSupportPage /></SuspenseWrapper> },
        ],
      },
    ],
  },
]);
